/**
 * Emoji, for the bookmark icons and the sticky notes.
 *
 * The picker used to be a text box saying "Paste an emoji", which is a
 * feature that works only for somebody who already knows how to open their
 * operating system's emoji panel — and on Windows that is a keyboard shortcut
 * most people have never been told. So the emoji are here, in the app, with a
 * search box over them.
 *
 * School first. A teacher reaching for an icon wants a pencil, a book, a bus
 * or an apple far more often than she wants a submarine, so those are the
 * first group and the rest follow. Every entry carries the words somebody
 * would actually type to find it, which is why the search finds ✏️ for
 * "write" and 🍎 for "teacher" as well as for their own names.
 */
/** Shorthand: "🍎 apple red fruit teacher" → one entry. */
function group(name, entries) {
    return {
        name,
        emoji: entries.map((entry) => {
            const at = entry.indexOf(' ');
            return at === -1
                ? { char: entry, keywords: '' }
                : { char: entry.slice(0, at), keywords: entry.slice(at + 1) };
        }),
    };
}
export const EMOJI_GROUPS = [
    group('School', [
        '🍎 apple red fruit teacher',
        '📚 books stack library reading',
        '📖 book open reading story',
        '📕 book red closed',
        '📗 book green closed',
        '📘 book blue closed',
        '📙 book orange closed',
        '📓 notebook',
        '📔 notebook decorated journal',
        '📒 ledger notebook',
        '📝 memo note writing pencil paper',
        '✏️ pencil write draw',
        '✒️ pen nib ink',
        '🖊️ pen ballpoint write',
        '🖍️ crayon colour colouring',
        '🖌️ paintbrush paint art',
        '📏 ruler straight measure maths',
        '📐 triangle ruler set square geometry',
        '✂️ scissors cut craft',
        '📌 pushpin pin',
        '📍 pin location round',
        '📎 paperclip attach',
        '🖇️ paperclips linked',
        '📅 calendar date',
        '📆 calendar tear off date',
        '🗓️ calendar spiral planner',
        '📋 clipboard checklist',
        '📁 folder file',
        '📂 folder open file',
        '🗂️ dividers files index',
        '🗃️ card box file index',
        '🗄️ filing cabinet',
        '📊 bar chart data graph',
        '📈 chart up growth increasing',
        '📉 chart down decreasing',
        '🔢 numbers input 1234 maths',
        '🔡 letters lowercase abcd',
        '🔠 letters uppercase capitals abcd',
        '🔤 letters abc alphabet',
        '🧮 abacus counting maths',
        '🎓 graduation cap mortarboard school',
        '🏫 school building',
        '🚌 bus school transport',
        '🎒 backpack rucksack bag school',
        '🖥️ computer desktop monitor',
        '💻 laptop computer',
        '⌨️ keyboard typing',
        '🖨️ printer print',
        '🔬 microscope science lab',
        '🔭 telescope space science',
        '🧪 test tube science experiment',
        '🧬 dna biology science',
        '🌡️ thermometer temperature',
        '🧲 magnet magnetic science',
        '🔍 magnifying glass search look find',
        '🔎 magnifying glass right search',
        '📣 megaphone announce loud',
        '📢 loudspeaker announce',
        '🔔 bell ring attention',
        '⏰ alarm clock time morning',
        '⏳ hourglass time waiting',
        '⌛ hourglass done time',
        '🕐 clock one time',
        '🗒️ notepad spiral list',
        '🗑️ wastebasket bin rubbish trash',
        '🎨 palette art paint colours',
        '🎭 masks drama theatre performing',
        '🎵 music note singing',
        '🎶 music notes singing song',
        '🎤 microphone singing sing',
        '🎹 keyboard piano music',
        '🥁 drum music percussion',
        '🎺 trumpet music brass',
        '🎻 violin music strings',
        '⚽ football soccer ball sport pe',
        '🏀 basketball sport pe',
        '⚾ baseball sport',
        '🏈 american football sport',
        '🏐 volleyball sport pe',
        '🎾 tennis sport',
        '🏓 table tennis ping pong',
        '🥎 softball sport',
        '🏃 running run pe exercise',
        '🤸 cartwheel gymnastics pe',
        '🧘 yoga meditation calm mindfulness',
    ]),
    group('Faces and people', [
        '😀 grin happy smile',
        '😃 smile happy open mouth',
        '😄 laugh happy smile eyes',
        '😁 beaming grin happy',
        '😆 laughing squinting happy',
        '😅 sweat smile relief nervous',
        '🤣 rolling laughing rofl',
        '😂 tears joy laughing crying',
        '🙂 slight smile',
        '🙃 upside down silly',
        '😉 wink',
        '😊 blush smile happy',
        '😇 halo angel innocent',
        '🥰 hearts love adoring',
        '😍 heart eyes love',
        '🤩 star struck amazed wow',
        '😘 kiss blowing',
        '😗 kissing',
        '🥲 smiling tear happy sad',
        '😋 yum tasty tongue',
        '😛 tongue out cheeky',
        '😜 wink tongue silly',
        '🤪 zany silly wild',
        '🤗 hug hugging',
        '🤭 hand over mouth oops giggle',
        '🤫 shush quiet shh',
        '🤔 thinking hmm wondering',
        '🤨 raised eyebrow suspicious',
        '😐 neutral straight face',
        '😑 expressionless',
        '😶 no mouth speechless',
        '🙄 eye roll',
        '😏 smirk',
        '😒 unamused',
        '😞 disappointed sad',
        '😔 pensive sad',
        '😪 sleepy tired',
        '🤤 drooling',
        '😴 sleeping asleep zzz',
        '😷 mask ill sick',
        '🤒 thermometer face ill sick fever',
        '🤕 bandage head hurt',
        '🤧 sneezing tissue cold',
        '🥵 hot overheated',
        '🥶 cold freezing',
        '😵 dizzy',
        '🤯 mind blown exploding head',
        '🤠 cowboy hat',
        '🥳 party face celebrate birthday',
        '😎 sunglasses cool',
        '🤓 nerd glasses',
        '🧐 monocle inspecting',
        '😕 confused',
        '😟 worried',
        '🙁 frown slight sad',
        '😮 open mouth surprised',
        '😯 hushed surprised',
        '😲 astonished shocked',
        '😳 flushed embarrassed',
        '🥺 pleading puppy eyes please',
        '😢 crying sad tear',
        '😭 sobbing crying loudly',
        '😤 triumph huffing',
        '😠 angry cross',
        '😡 rage furious red',
        '🤬 swearing symbols cursing',
        '😱 scream fear',
        '😨 fearful scared',
        '😰 anxious sweat worried',
        '😥 sad relieved disappointed',
        '🤥 lying pinocchio',
        '🤡 clown',
        '👻 ghost halloween boo',
        '💀 skull',
        '👽 alien space',
        '🤖 robot',
        '💩 poop silly',
        '👶 baby',
        '🧒 child kid',
        '👦 boy',
        '👧 girl',
        '🧑 person adult',
        '👨 man',
        '👩 woman',
        '🧓 older person',
        '👨‍🏫 man teacher teaching',
        '👩‍🏫 woman teacher teaching',
        '👨‍🎓 man student graduate',
        '👩‍🎓 woman student graduate',
        '👪 family',
        '🧑‍🤝‍🧑 people holding hands friends',
        '👋 wave hello hi bye',
        '🤚 raised back hand',
        '✋ raised hand stop',
        '🖐️ hand fingers splayed',
        '👌 ok perfect',
        '🤏 pinching small little',
        '✌️ victory peace',
        '🤞 fingers crossed luck hope',
        '🤟 love you sign',
        '🤘 rock horns',
        '🤙 call me shaka',
        '👈 point left',
        '👉 point right',
        '👆 point up',
        '👇 point down',
        '☝️ index pointing up one',
        '👍 thumbs up yes good like',
        '👎 thumbs down no bad',
        '✊ fist raised',
        '👊 fist bump punch',
        '👏 clap applause well done',
        '🙌 raising hands celebrate hooray',
        '🤝 handshake deal agree',
        '🙏 pray thanks please namaste',
        '💪 muscle strong flex',
        '🧠 brain thinking smart',
        '👀 eyes look watch',
        '👂 ear listen listening',
        '👣 footprints steps',
    ]),
    group('Animals and nature', [
        '🐶 dog puppy',
        '🐱 cat kitten',
        '🐭 mouse',
        '🐹 hamster',
        '🐰 rabbit bunny',
        '🦊 fox',
        '🐻 bear',
        '🐼 panda',
        '🐨 koala',
        '🐯 tiger',
        '🦁 lion',
        '🐮 cow',
        '🐷 pig',
        '🐸 frog',
        '🐵 monkey',
        '🙈 see no evil monkey',
        '🙉 hear no evil monkey',
        '🙊 speak no evil monkey',
        '🐔 chicken hen',
        '🐧 penguin',
        '🐦 bird',
        '🦆 duck',
        '🦉 owl wise',
        '🦇 bat halloween',
        '🐺 wolf',
        '🐗 boar',
        '🐴 horse',
        '🦄 unicorn magic',
        '🐝 bee busy honey',
        '🐛 caterpillar bug',
        '🦋 butterfly',
        '🐌 snail slow',
        '🐞 ladybird ladybug beetle',
        '🐜 ant',
        '🕷️ spider',
        '🐢 turtle tortoise slow',
        '🐍 snake',
        '🦎 lizard gecko',
        '🐙 octopus',
        '🦑 squid',
        '🦐 shrimp',
        '🦀 crab',
        '🐠 tropical fish',
        '🐟 fish',
        '🐬 dolphin',
        '🐳 whale',
        '🦈 shark',
        '🐊 crocodile alligator',
        '🐘 elephant',
        '🦒 giraffe',
        '🦓 zebra',
        '🦘 kangaroo',
        '🐪 camel',
        '🐄 cow full',
        '🐖 pig full',
        '🐑 sheep',
        '🐐 goat',
        '🦌 deer',
        '🐕 dog full',
        '🐈 cat full',
        '🐇 rabbit full',
        '🌵 cactus',
        '🌲 evergreen tree pine',
        '🌳 tree deciduous',
        '🌴 palm tree',
        '🌱 seedling growing plant sprout',
        '🌿 herb leaves plant',
        '☘️ shamrock clover',
        '🍀 four leaf clover luck',
        '🍁 maple leaf autumn fall',
        '🍂 fallen leaves autumn fall',
        '🍃 leaf wind blowing',
        '🌾 wheat harvest',
        '🌷 tulip flower',
        '🌹 rose flower',
        '🥀 wilted flower',
        '🌺 hibiscus flower',
        '🌸 cherry blossom flower spring',
        '🌼 daisy blossom flower',
        '🌻 sunflower flower',
        '🌞 sun face sunny',
        '🌝 full moon face',
        '🌙 crescent moon night',
        '⭐ star',
        '🌟 glowing star sparkle',
        '✨ sparkles magic shiny',
        '⚡ lightning bolt electric fast',
        '🔥 fire hot flame',
        '💧 droplet water drop',
        '🌊 wave ocean sea water',
        '☀️ sun sunny bright',
        '🌤️ sun behind cloud',
        '⛅ partly cloudy sun',
        '☁️ cloud cloudy',
        '🌧️ rain raining cloud',
        '⛈️ thunderstorm storm',
        '🌨️ snow cloud snowing',
        '❄️ snowflake snow cold winter',
        '⛄ snowman snow winter',
        '🌈 rainbow colours pride',
        '🌍 earth globe world europe africa',
        '🌎 earth globe americas world',
        '🌏 earth globe asia world',
    ]),
    group('Food', [
        '🍏 green apple',
        '🍐 pear',
        '🍊 orange tangerine',
        '🍋 lemon',
        '🍌 banana',
        '🍉 watermelon',
        '🍇 grapes',
        '🍓 strawberry',
        '🫐 blueberries',
        '🍈 melon',
        '🍒 cherries',
        '🍑 peach',
        '🥭 mango',
        '🍍 pineapple',
        '🥥 coconut',
        '🥝 kiwi',
        '🍅 tomato',
        '🥕 carrot',
        '🌽 corn sweetcorn',
        '🥦 broccoli',
        '🥒 cucumber',
        '🥬 lettuce greens',
        '🥔 potato',
        '🍞 bread loaf',
        '🥐 croissant',
        '🥨 pretzel',
        '🧀 cheese',
        '🥚 egg',
        '🍳 fried egg cooking',
        '🥞 pancakes',
        '🧇 waffle',
        '🥓 bacon',
        '🍔 burger hamburger',
        '🍟 chips fries',
        '🍕 pizza',
        '🌭 hot dog',
        '🥪 sandwich',
        '🌮 taco',
        '🌯 burrito wrap',
        '🥗 salad',
        '🍿 popcorn film movie',
        '🧂 salt',
        '🍝 pasta spaghetti',
        '🍜 noodles ramen soup',
        '🍲 stew pot',
        '🍚 rice',
        '🍱 bento lunch box',
        '🍣 sushi',
        '🍤 prawn shrimp fried',
        '🍦 ice cream soft serve',
        '🍨 ice cream bowl',
        '🍩 doughnut donut',
        '🍪 biscuit cookie',
        '🎂 birthday cake',
        '🧁 cupcake',
        '🍰 cake slice',
        '🥧 pie',
        '🍫 chocolate bar',
        '🍬 sweet candy',
        '🍭 lollipop',
        '🍯 honey pot',
        '🥛 milk glass',
        '☕ coffee tea hot drink',
        '🍵 tea green cup',
        '🧃 juice box drink',
        '🥤 cup straw drink soda',
        '🧋 bubble tea boba',
        '🍎 apple teacher red',
    ]),
    group('Things and places', [
        '🏠 house home',
        '🏡 house garden home',
        '🏢 office building',
        '🏥 hospital',
        '🏦 bank',
        '🏪 shop convenience store',
        '⛪ church',
        '🏛️ classical building museum',
        '⛺ tent camping',
        '🌋 volcano',
        '🏔️ mountain snow',
        '🏖️ beach umbrella holiday',
        '🎪 circus tent',
        '🎡 ferris wheel fair',
        '🎢 rollercoaster fair',
        '🚂 train steam locomotive',
        '🚗 car',
        '🚕 taxi',
        '🚑 ambulance',
        '🚒 fire engine',
        '🚓 police car',
        '🚲 bicycle bike',
        '🛴 scooter',
        '✈️ aeroplane plane flight',
        '🚀 rocket space launch',
        '🛸 flying saucer ufo',
        '⛵ sailing boat',
        '🚢 ship',
        '⚓ anchor',
        '🎁 gift present',
        '🎈 balloon party',
        '🎉 party popper celebrate',
        '🎊 confetti ball celebrate',
        '🎀 ribbon bow',
        '🏆 trophy winner prize',
        '🥇 first place gold medal',
        '🥈 second place silver medal',
        '🥉 third place bronze medal',
        '🎖️ medal military award',
        '🏅 sports medal award',
        '👑 crown king queen',
        '💎 gem diamond',
        '🔑 key',
        '🔒 locked padlock',
        '🔓 unlocked padlock open',
        '💡 light bulb idea',
        '🔦 torch flashlight',
        '🕯️ candle',
        '🧸 teddy bear toy',
        '🪁 kite',
        '🎲 dice game',
        '🧩 puzzle piece jigsaw',
        '♟️ chess pawn',
        '🎯 target dart bullseye goal',
        '🎮 game controller video game',
        '📷 camera photo',
        '📹 video camera film',
        '📺 television tv',
        '📻 radio',
        '☎️ telephone phone',
        '📱 mobile phone smartphone',
        '💰 money bag',
        '💵 money dollar note',
        '💳 card credit payment',
        '📦 parcel box package',
        '✉️ envelope letter mail',
        '📧 email',
        '📮 postbox mail',
        '🧹 broom sweeping tidy',
        '🧺 basket laundry',
        '🧼 soap washing hands',
        '🩹 plaster bandage first aid',
        '💊 pill medicine',
        '🩺 stethoscope doctor nurse',
        '🧴 bottle lotion sanitiser',
    ]),
    group('Symbols', [
        '❤️ red heart love',
        '🧡 orange heart',
        '💛 yellow heart',
        '💚 green heart',
        '💙 blue heart',
        '💜 purple heart',
        '🖤 black heart',
        '🤍 white heart',
        '🤎 brown heart',
        '💖 sparkling heart',
        '💗 growing heart',
        '💕 two hearts',
        '💞 revolving hearts',
        '💝 heart with ribbon gift',
        '💯 hundred points perfect score',
        '✅ tick check done yes',
        '☑️ ballot box tick checked',
        '✔️ heavy tick check',
        '❌ cross wrong no',
        '❎ cross mark button no',
        '⭕ circle heavy',
        '❗ exclamation important',
        '❓ question',
        '❕ exclamation white',
        '❔ question white',
        '‼️ double exclamation',
        '⚠️ warning caution',
        '🚫 prohibited no forbidden',
        '⛔ no entry stop',
        '🔴 red circle',
        '🟠 orange circle',
        '🟡 yellow circle',
        '🟢 green circle',
        '🔵 blue circle',
        '🟣 purple circle',
        '⚫ black circle',
        '⚪ white circle',
        '🟥 red square',
        '🟧 orange square',
        '🟨 yellow square',
        '🟩 green square',
        '🟦 blue square',
        '🟪 purple square',
        '⬛ black square',
        '⬜ white square',
        '🔺 red triangle up',
        '🔻 red triangle down',
        '🔶 orange diamond',
        '🔷 blue diamond',
        '⬆️ arrow up',
        '⬇️ arrow down',
        '⬅️ arrow left',
        '➡️ arrow right',
        '🔄 arrows counterclockwise refresh repeat',
        '🔁 repeat loop',
        '▶️ play',
        '⏸️ pause',
        '⏹️ stop',
        '➕ plus add',
        '➖ minus subtract',
        '➗ divide division',
        '✖️ multiply times',
        '♾️ infinity forever',
        '🆕 new',
        '🆗 ok button',
        '🆘 sos help',
        '🔝 top up',
        '🎗️ ribbon awareness',
        '♻️ recycling recycle',
        '🕊️ dove peace',
        '⚖️ scales balance justice fair',
        '🔗 link chain',
        '📛 name badge',
        '🏳️‍🌈 rainbow flag pride',
    ]),
];
/** Every emoji, flattened — for counting and for a search with no groups. */
export const ALL_EMOJI = EMOJI_GROUPS.flatMap((g) => g.emoji);
/**
 * The emoji matching what she typed.
 *
 * Matched on the keywords as well as the character, so "teacher" finds the
 * apple and the two teacher figures. An empty search gives everything back,
 * grouped, because browsing is how most people pick an emoji.
 */
export function searchEmoji(term) {
    const wanted = term.trim().toLowerCase();
    if (!wanted)
        return EMOJI_GROUPS;
    return EMOJI_GROUPS.map((g) => ({
        name: g.name,
        emoji: g.emoji.filter((e) => e.char === wanted || e.keywords.includes(wanted)),
    })).filter((g) => g.emoji.length > 0);
}
//# sourceMappingURL=emoji.js.map